// import Vue from "vue";
// export function getComponentHtml(component, props) {
//   // Create a temporary container element
//   const container = document.createElement("div");
//   // Instantiate and mount the component
//   const ComponentConstructor = Vue.extend(component);
//   const instance = new ComponentConstructor({
//     propsData: props,
//     store
//   }).$mount(container);
//   // Extract the rendered HTML
//   const htmlString = instance.$el.outerHTML;
//   // Optionally, destroy the instance
//   instance.$destroy();
//   return htmlString;
// }
import { createApp } from "vue";

import store from "@/vuex/store";

interface GetComponentHtmlOptions {
  // Installs an app.config.errorHandler on this throwaway app and rethrows a
  // captured render error to the caller after unmount. Vue's production build
  // swallows an unhandled render error to console.error and returns a partial
  // wrapper when no errorHandler is set and throwUnhandledErrorInProduction is
  // off, so a bare try/catch at the call site never fires in a vite-build
  // bundle -- installing a handler makes the outcome the same in both builds,
  // which is the whole point: containment must not depend on which build is
  // running. Absent (the default for every pre-existing caller), behaviour is
  // byte-for-byte what it is today.
  rethrowRenderErrors?: boolean;
}

export function getComponentHtml(
  component,
  props,
  options?: GetComponentHtmlOptions
) {
  // Create a container
  const container = document.createElement("div");

  // Create app instance and pass props via a wrapper
  const app = createApp(component, props);

  // Provide the store if necessary (e.g., Vuex or Pinia)
  app.use(store);

  // Recorded here, not rethrown from inside the handler: Vue invokes the
  // handler through callWithErrorHandling(errorHandler, null, ...) with a null
  // instance, so a throw from inside it goes to Vue's own logError rather than
  // looping back through this same handler. Rethrowing immediately would
  // escape before container.outerHTML is read and before the app is
  // unmounted, leaking a mounted throwaway app on every bad card -- recording
  // and rethrowing after the finally block below keeps teardown on the
  // normal path.
  let capturedError: unknown;
  if (options?.rethrowRenderErrors) {
    app.config.errorHandler = (err) => {
      capturedError = err;
    };
  }

  let htmlString: string;
  let renderError: unknown;
  try {
    // Mount the component to the container
    app.mount(container);
    // Get HTML
    htmlString = container.outerHTML;
  } finally {
    // Only the MOUNT phase decides whether this render failed. The handler
    // above catches every error Vue routes through it, teardown included, so
    // reading capturedError after unmount let an onUnmounted throw -- or a
    // watcher flushed during teardown -- discard an htmlString that was
    // already correct, and the caller drew an empty fallback card instead of
    // the good one it had.
    renderError = capturedError;
    // Clean up whether or not the render succeeded
    app.unmount();
  }

  if (renderError !== undefined) {
    throw renderError;
  }

  return htmlString;
}
